/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Wifi, WifiOff, RefreshCw, CloudUpload, AlertTriangle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useOfflineSync } from '@/hooks/useOfflineSync';

/**
 * OfflineStatusBadge — mostra rete online/offline e operazioni in coda.
 * Click = forza la sincronizzazione.
 */
export const OfflineStatusBadge = ({ className }) => {
  const sync = useOfflineSync();
  const failed = sync.failed || 0;
  const Icon = !sync.online ? WifiOff : sync.syncing ? RefreshCw : failed > 0 ? AlertTriangle : sync.pending > 0 ? CloudUpload : Wifi;
  const label = !sync.online
    ? `Offline · ${sync.pending} in coda`
    : sync.syncing ? 'Sincronizzazione…'
      : failed > 0 ? `${failed} non inviati · riprova`
        : sync.pending > 0 ? `${sync.pending} da sincronizzare` : 'Sincronizzato';
  const onClick = () => (failed > 0 ? sync.retryFailed() : sync.flush());

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-xs font-medium transition-colors',
              !sync.online ? 'border-warning/50 bg-warning/10 text-warning'
                : failed > 0 ? 'border-destructive/40 bg-destructive/10 text-destructive hover:border-destructive/60'
                  : 'border-border/70 bg-card hover:border-primary/40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              className,
            )}
            data-testid="offline-status-badge"
            aria-label={label}
          >
            <Icon className={cn('h-3.5 w-3.5', sync.syncing && 'animate-spin')} />
            <span data-testid="offline-status-label">{label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent className="text-xs">
          Local-first: i dati sono salvati sul dispositivo e sincronizzati appena torna la rete.
          {failed > 0 && <div className="text-destructive mt-1">{failed} operazioni hanno esaurito i tentativi: clicca per riprovare.</div>}
          {sync.lastSyncAt && <div className="text-muted-foreground mt-1">Ultimo sync: {new Date(sync.lastSyncAt).toLocaleTimeString('it-IT')}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default OfflineStatusBadge;
